import { CssBaseline, ThemeProvider as MuiThemeProvider, createTheme } from '@mui/material'
import { toMuiTheme, type Tokens } from '@commerce/tokens'
import { useMemo, type PropsWithChildren } from 'react'
import { yaloMuiTheme } from '../design/yaloTheme'

/**
 * Two layers, in this order:
 *
 * 1. `yaloMuiTheme()` — the design system: type scale, radii, shadows, and the
 *    component defaults that make a Button a pill and a Card flat.
 * 2. `toMuiTheme(tokens)` — what *this storefront* authored, which wins.
 *
 * They are composed into **one options object** and created once.
 * `createTheme(a, b)` looks like it would do the same and does not: later
 * arguments are deep-merged onto the *finished* theme, so a plain `spacing: 8`
 * in the second one replaces the spacing **function** `createTheme` just built
 * with the number 8. Everything that calls `theme.spacing(2)` — Grid2's column
 * spacing, for one — then dies with `_a.call is not a function`.
 *
 * That is not hypothetical: it shipped, every unit test stayed green because
 * they mount components without this provider, and the catalog crashed on the
 * first real page load. `ThemeProvider.test.tsx` renders a Grid through the
 * real provider for exactly this reason.
 */
export const ThemeProvider = ({ tokens, children }: PropsWithChildren<{ tokens: Tokens }>) => {
  const theme = useMemo(() => createTheme(yaloMuiTheme(toMuiTheme(tokens))), [tokens])

  return (
    <MuiThemeProvider theme={theme}>
      <CssBaseline />
      {children}
    </MuiThemeProvider>
  )
}
